import { useMutation, useQueryClient } from '@tanstack/react-query';
import { watchApi } from '../api/watchApi';
import { WATCHES_QUERY_KEY } from './useWatches';
import type { WatchListResponse, ApiError } from '../types/showcase';

interface ToggleWatchVars {
  moduleCode: string;
  /** 토글 후 원하는 구독 상태 — true=구독(PUT) · false=해제(DELETE) */
  nextSubscribed: boolean;
}

interface ToggleWatchContext {
  previous?: WatchListResponse;
}

/**
 * PUT/DELETE /api/eraf/showcase/watches/{moduleCode} — queue #677 §UAT-2026-07-13-R677 §R677-4①
 * 낙관적 토글 뮤테이션. `onMutate` 가 `['showcase','watches']` 캐시를 즉시 add/remove 반전하고,
 * `onError` 는 이전 스냅샷으로 rollback, `onSuccess` 는 서버가 반환한 확정 `moduleCodes` 로
 * 캐시를 덮어써 정합을 맞춘다(§R677-3 응답 shape — PUT/DELETE 모두 갱신된 전체 목록 반환).
 * `onSettled` invalidate 는 §R662 훅 계보 답습(캐시-확정 이후 재검증).
 */
export function useToggleWatch() {
  const queryClient = useQueryClient();

  return useMutation<WatchListResponse, ApiError, ToggleWatchVars, ToggleWatchContext>({
    mutationFn: ({ moduleCode, nextSubscribed }) =>
      nextSubscribed ? watchApi.watch(moduleCode) : watchApi.unwatch(moduleCode),

    onMutate: async ({ moduleCode, nextSubscribed }) => {
      await queryClient.cancelQueries({ queryKey: WATCHES_QUERY_KEY });
      const previous = queryClient.getQueryData<WatchListResponse>(WATCHES_QUERY_KEY);
      const prevCodes = previous?.moduleCodes ?? [];
      const nextCodes = nextSubscribed
        ? [...new Set([...prevCodes, moduleCode])].sort()
        : prevCodes.filter((c) => c !== moduleCode);
      queryClient.setQueryData<WatchListResponse>(WATCHES_QUERY_KEY, { moduleCodes: nextCodes });
      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(WATCHES_QUERY_KEY, context.previous);
      }
    },

    onSuccess: (data) => {
      queryClient.setQueryData(WATCHES_QUERY_KEY, data);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: WATCHES_QUERY_KEY });
    },
  });
}
